import Avatar from '-/Custom/Avatar';
import Icon from '-/Unistyles/Icon';
import { useLocalSearchParams } from 'expo-router';
import { useFormContext } from 'react-hook-form';
import { Pressable } from 'react-native';
import Sortable from 'react-native-sortables';
import { StyleSheet } from 'react-native-unistyles';
import { BadgeAssigneeEditAttributeFragment } from '@/components/Badge/BadgeAssigneeEditAttribute';
import { EventScorekeepingPanelBadgeItemFragment } from '@/components/Event/EventScorekeeping/EventScorekeepingPanel/EventScorekeepingPanelBadge/EventScorekeepingPanelBadgeItem';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import Tag from '@/components/Unistyles/Tag';
import { graphql, readFragment } from '@/gql/graphql';
import signNumber from '@/utils/signNumber';
import type {
  EventScorekeepingPanelFormValues,
  EventScorekeepingPanelRecipient,
} from '../../EventScorekeeping.types';

import EventScorekeepingPanelPreviewRecipientItemAttribute from './EventScorekeepingPanelPreviewRecipientItemAttribute';

export const EventScorekeepingPanelPreviewRecipientItemFragment = graphql(
  /* GraphQL */ `
    fragment EventScorekeepingPanelPreviewRecipientItem on badge_assignee {
      id
      person_team {
        person_id
        team_id
        jersey_number
        person {
          id
          name
          avatar_id
        }
        team {
          id
          home_primary_color
        }
      }
      badge_assignee_attributes {
        id
        value
        badge_attribute {
          id
          attribute_type
        }
      }
      badge {
        id
        title
        color
        can_quick_assign
        attribute_score {
          id
          title
          default_numeric_value
        }
        badge_attributes(order_by: { order: asc }) {
          id
          title
          options
          is_required
          attribute_type
          default_value
        }
      }
    }
  `,
);

function EventScorekeepingPanelPreviewRecipientItem({
  recipient,
  index,
  isSelected,
  hasSiblings,
}: {
  recipient: EventScorekeepingPanelRecipient;
  index: number;
  isSelected: boolean;
  hasSiblings: boolean;
}) {
  const { badgeAssigneeId } = useLocalSearchParams<{
    badgeAssigneeId?: string;
  }>();

  const {
    setValue,
    getValues,
    formState: { errors, defaultValues },
    reset,
  } = useFormContext<EventScorekeepingPanelFormValues>();

  const onRemove = () => {
    const currentRecipients = getValues('recipients');
    const selectedRecipientId = getValues('selectedRecipientId');

    if (selectedRecipientId === recipient.id) {
      setValue('selectedRecipientId', null);
    }

    if (badgeAssigneeId && !recipient.isNew) {
      setValue(`recipients.${index}.isDeleted`, !recipient.isDeleted, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else if (currentRecipients.length === 1) {
      setValue('selectedRecipientId', null);

      reset(
        {
          ...defaultValues,
          selectedTeam: getValues('selectedTeam'),
          selectedTime: getValues('selectedTime'),
        },
        { keepDefaultValues: true },
      );
    } else {
      setValue(
        'recipients',
        currentRecipients.filter((_, i) => i !== index),
      );
    }
  };

  const badge = readFragment(
    EventScorekeepingPanelBadgeItemFragment,
    recipient.badge,
  );

  const onPress = () => {
    if (isSelected) {
      setValue('selectedRecipientId', null);
      return;
    }

    setValue('selectedRecipientId', recipient.id);
  };

  const scoreAssigneeAttribute = recipient.badge_assignee_attributes.find(
    (attribute) => attribute.badge_attribute.id === badge.attribute_score?.id,
  );

  const hasError = errors.recipients?.[
    index
  ]?.badge_assignee_attributes?.some?.((attribute) => attribute?.value);

  return (
    <Pressable>
      <Box
        style={style.container(recipient.isDeleted)}
        space="md"
        px="lg"
        py="md"
      >
        <Box
          direction="horizontal"
          justifyContent="space-between"
          space="sm"
          alignItems="center"
        >
          <Box direction="horizontal" space="lg" alignItems="center">
            <Text style={style.index} size="lg" variant="typographySecondary">
              {index + 1}
            </Text>
            <Avatar
              color={recipient.person_team?.team?.home_primary_color || ''}
              placeholderText={recipient.person_team?.jersey_number || ''}
              size="lg"
            />
            <Box>
              <Box direction="horizontal" space="md">
                <Text
                  style={style.badgeTitle(recipient.isDeleted)}
                  numberOfLines={1}
                  size="2xl"
                >
                  {badge?.title}
                </Text>
                {scoreAssigneeAttribute?.value ? (
                  <Box direction="horizontal" space="xs" alignItems="center">
                    <Text
                      style={style.scoreAssigneeAttribute(recipient.isDeleted)}
                      size="sm"
                      variant="success"
                    >
                      ({badge.attribute_score?.title}{' '}
                      {signNumber(
                        Number.parseInt(scoreAssigneeAttribute.value, 10),
                        false,
                      )}
                      )
                    </Text>
                  </Box>
                ) : null}
              </Box>

              {recipient.person_team?.person?.name ? (
                <Text
                  style={style.personName(recipient.isDeleted)}
                  numberOfLines={1}
                  size="xl"
                >
                  {recipient.person_team?.person?.name}
                </Text>
              ) : null}
            </Box>
          </Box>

          <Box direction="horizontal" space="md">
            {badgeAssigneeId && recipient.isNew ? (
              <Tag label="adding" variant="success" />
            ) : null}
            {badgeAssigneeId && recipient.isDeleted ? (
              <Tag label="removing" variant="error" />
            ) : null}
            {badge.badge_attributes.length > 0 && !recipient.isDeleted ? (
              <Sortable.Touchable onTap={onPress}>
                <Icon
                  name="create-outline"
                  variant={hasError ? 'error' : undefined}
                />
              </Sortable.Touchable>
            ) : null}
            {hasSiblings && !recipient.isDeleted ? (
              <Sortable.Touchable onTap={onRemove}>
                <Icon name="trash-outline" />
              </Sortable.Touchable>
            ) : null}
            {recipient.isDeleted ? (
              <Sortable.Touchable onTap={onRemove}>
                <Icon name="arrow-undo-outline" />
              </Sortable.Touchable>
            ) : null}
          </Box>
        </Box>

        {isSelected ? (
          <Box space="md">
            {badge?.badge_attributes.map((attributeFragment) => {
              const attribute = readFragment(
                BadgeAssigneeEditAttributeFragment,
                attributeFragment,
              );

              return (
                <EventScorekeepingPanelPreviewRecipientItemAttribute
                  key={attribute.id}
                  attribute={attribute}
                  index={index}
                />
              );
            })}
          </Box>
        ) : null}
      </Box>
    </Pressable>
  );
}

export default EventScorekeepingPanelPreviewRecipientItem;

const style = StyleSheet.create((theme) => ({
  container: (isDeleted: boolean) => ({
    borderTopWidth: theme.size(0.5),
    borderTopColor: theme.colors.borderMuted,
    opacity: isDeleted ? 0.6 : 1,
  }),
  index: {
    width: theme.size(2.5),
  },
  badgeTitle: (isDeleted: boolean) => ({
    textDecorationLine: isDeleted ? 'line-through' : 'none',
    textDecorationColor: theme.colors.borderMuted,
  }),
  personName: (isDeleted: boolean) => ({
    textDecorationLine: isDeleted ? 'line-through' : 'none',
    textDecorationColor: theme.colors.borderMuted,
  }),
  scoreAssigneeAttribute: (isDeleted: boolean) => ({
    textDecorationLine: isDeleted ? 'line-through' : 'none',
    textDecorationColor: theme.colors.borderMuted,
    color: isDeleted ? theme.colors.typographyMuted : theme.colors.success,
  }),
}));
